import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { TextField, Checkbox } from '@mui/material';
import {
  Card, CardBody, Button, Input, InputGroup, InputGroupText,
  Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, UncontrolledDropdown, DropdownToggle,
  DropdownMenu, DropdownItem, Collapse
} from 'reactstrap';
import axios from '../../../store/axios';
import { lineHeight, textAlign } from '../../../../node_modules/@mui/system/index';

const Providers = () => {
  // State for providers
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // State for provider modal
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerForm, setProviderForm] = useState({
    label: '',
    modelName: '',
    provider: 'OpenRouter',
    type: 'text',
    cost: 0,
    currency: 'USD',
    latency: 0,
    aCost: 0
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  // State for voices
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [voices, setVoices] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voiceDeleteModal, setVoiceDeleteModal] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);
  const [voiceForm, setVoiceForm] = useState({
    voiceName: '',
    voiceKey: '',
    providerId: '',
    gender: 'neutral',
    accent: '',
    demoUrl: '',
    voiceProvider: 'Cartesia'
  });
  const [voiceImageFile, setVoiceImageFile] = useState(null);
  const [voiceImagePreview, setVoiceImagePreview] = useState(null);

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, [currentPage, searchTerm, typeFilter]);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/billing/providers', {
        params: { page: currentPage, limit: 20, search: searchTerm, type: typeFilter }
      });

      if (response.data.code === 200) {
        setProviders(response.data.body.providers);
        setTotalPages(response.data.body.pagination.pages);
      }
    } catch (error) {
      toast.error('Failed to load providers');
      console.error(error);
    }
    setLoading(false);
  };

  // Load voices for a provider
  const loadVoicesForProvider = async (providerId) => {
    try {
      const response = await axios.get('/admin/billing/voices', {
        params: { providerId, limit: 100 }
      });

      if (response.data.code === 200) {
        setVoices(prev => ({ ...prev, [providerId]: response.data.body.voices }));
      }
    } catch (error) {
      toast.error('Failed to load voices');
    }
  };

  // Toggle voice list expansion
  const toggleProviderVoices = (providerId) => {
    if (expandedProvider === providerId) {
      setExpandedProvider(null);
    } else {
      setExpandedProvider(providerId);
      if (!voices[providerId]) {
        loadVoicesForProvider(providerId);
      }
    }
  };

  // Handle provider form
  const handleProviderChange = (e) => {
    setProviderForm({ ...providerForm, [e.target.name]: e.target.value });
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload JPG, PNG, SVG, or GIF');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 5MB');
        return;
      }

      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const openProviderModal = (provider = null) => {
    if (provider) {
      setSelectedProvider(provider);
      setProviderForm({
        label: provider.label,
        modelName: provider.modelName || '',
        provider: provider.provider,
        type: provider.type,
        cost: provider.cost,
        currency: provider.currency,
        latency: provider.latency,
        aCost: provider.aCost || 0
      });
      setIconPreview(provider.icon);
    } else {
      setSelectedProvider(null);
      setProviderForm({
        label: '',
        modelName: '',
        provider: 'OpenRouter',
        type: 'text',
        cost: 0,
        currency: 'USD',
        latency: 0,
        aCost: 0
      });
      setIconPreview(null);
    }
    setIconFile(null);
    setShowProviderModal(true);
  };

  const handleProviderSubmit = async () => {
    try {
      const formData = new FormData();
      Object.keys(providerForm).forEach(key => {
        formData.append(key, providerForm[key]);
      });

      if (iconFile) {
        formData.append('icon', iconFile);
      }

      const url = selectedProvider
        ? `/admin/billing/providers/${selectedProvider._id}`
        : '/admin/billing/providers';
      const method = selectedProvider ? 'put' : 'post';

      const response = await axios[method](url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.code === 200) {
        toast.success(selectedProvider ? 'Provider updated' : 'Provider created');
        setShowProviderModal(false);
        loadProviders();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleProviderDelete = async (providerId) => {
    try {
      const response = await axios.delete(`/admin/billing/providers/${providerId}`);
      if (response.data.code === 200) {
        toast.success('Provider deleted');
        loadProviders();
      }
    } catch (error) {
      if (error.response?.data?.body?.canDelete === false) {
        // Show warning with usage info
        const { voiceCount, agentCount } = error.response.data.body;
        const forceDelete = window.confirm(
          `This provider is in use:\n- ${voiceCount} voices\n- ${agentCount} agents\n\nDelete anyway?`
        );

        if (forceDelete) {
          try {
            await axios.delete(`/admin/billing/providers/${providerId}?force=true`);
            toast.success('Provider deleted (forced)');
            loadProviders();
          } catch (err) {
            toast.error('Delete failed');
          }
        }
      } else {
        toast.error('Delete failed');
      }
    }
  };

  // Voice management
  const openVoiceModal = (voice = null, providerId = null) => {
    if (voice) {
      setSelectedVoice(voice);
      setVoiceForm({
        voiceName: voice.voiceName,
        voiceKey: voice.voiceKey,
        providerId: voice.providerId._id || voice.providerId,
        gender: voice.gender,
        accent: voice.accent || '',
        demoUrl: voice.demoUrl || '',
        voiceProvider: voice.voiceProvider
      });
      setVoiceImagePreview(voice.voiceImg);
    } else {
      setSelectedVoice(null);
      setVoiceForm({
        voiceName: '',
        voiceKey: '',
        providerId: providerId || '',
        gender: 'neutral',
        accent: '',
        demoUrl: '',
        voiceProvider: 'Cartesia'
      });
      setVoiceImagePreview(null);
    }
    setVoiceImageFile(null);
    setShowVoiceModal(true);
  };

  const handleVoiceSubmit = async () => {
    try {
      const formData = new FormData();
      Object.keys(voiceForm).forEach(key => {
        formData.append(key, voiceForm[key]);
      });

      if (voiceImageFile) {
        formData.append('voiceImg', voiceImageFile);
      }

      const url = selectedVoice
        ? `/admin/billing/voices/${selectedVoice._id}`
        : '/admin/billing/voices';
      const method = selectedVoice ? 'put' : 'post';

      const response = await axios[method](url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.code === 200) {
        toast.success(selectedVoice ? 'Voice updated' : 'Voice created');
        setShowVoiceModal(false);
        loadVoicesForProvider(voiceForm.providerId);
        loadProviders(); // Refresh to update voice counts
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleVoiceDelete = async (voiceId, providerId) => {

    try {
      await axios.delete(`/admin/billing/voices/${voiceId}`);
      toast.success('Voice deleted');
      loadVoicesForProvider(providerId);
      loadProviders();
    } catch (error) {
      if (error.response?.data?.body?.canDelete === false) {
        const forceDelete = window.confirm(
          `Voice is in use by ${error.response.data.body.agentCount} agents. Delete anyway?`
        );
        if (forceDelete) {
          await axios.delete(`/admin/billing/voices/${voiceId}?force=true`);
          toast.success('Voice deleted');
          loadVoicesForProvider(providerId);
          loadProviders();
        }
      } else {
        toast.error('Delete failed');
      }
    }
  };

  return (
    <div className="content-dashboard">
      <div className="title-area style-workshop">
        <div>
          <h4>Voice & Text Providers</h4>
          <p className="text-muted">Manage AI model providers and voice configurations</p>
        </div>
      </div>

      <div className="table-container filters-table">
        <div className='header-table-filters advanced-table-filters'>
          <div className='left-area' style={{ width: '70%' }}>
            <div className='filter-box'>
              <div className='searchbox'>
                <TextField
                  fullWidth
                  hiddenLabel
                  size="small"
                  name='search'
                  variant="standard"
                  placeholder="Search providers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className='icon'>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                    <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                  </svg>
                </i>
              </div>
            </div>
            <div className='filter-box d-flex gap-3'>

              {/* Status Filter */}
              <Input type="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="text">Text/LLM</option>
                <option value="tts">Text-to-Speech</option>
                <option value="stt">Speech-to-Text</option>
                <option value="engine">Engine</option>
                <option value="voip">VoIP</option>
              </Input>
            </div>
          </div>
          <div className='right-deals-filtes' style={{ width: '30%' }}>
            <button type="button" className="btn-style-new" onClick={() => openProviderModal()}>
              <i className="fa fa-plus mr-1"></i> New Provider
            </button>
          </div>
        </div>
        <div className='table-area'>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Icon</th>
                  <th>Providers</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Model Name</th>
                  <th>Applied Cost</th>
                  <th>Actual Cost</th>
                  <th>Latency</th>
                  <th>Voices</th>
                  <th>Agents</th>
                  <th style={{ width: '20px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map(provider => (
                  <React.Fragment key={provider._id}>
                    <tr>
                      <td style={{ textAlign: 'center' }}>
                        {provider.icon ? <img src={provider.icon} alt={provider.label} style={{ width: '25px', height: '25px', objectFit: 'cover', borderRadius: '8px' }} />
                        :
                          <div style={{ width: '30px', height: '30px', background: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                            <i className="fa fa-image" style={{ fontSize: '20px', color: '#999' }}></i>
                          </div>
                        }
                      </td>
                      <td><strong>{provider.provider}</strong></td>
                      <td>{provider.label}</td>
                      <td>
                        <Badge color={
                          provider.type === 'text' ? 'primary' :
                            provider.type === 'tts' ? 'success' :
                              provider.type === 'stt' ? 'info' : 'secondary'
                        }>
                          {provider.type.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="text-muted small">{provider.modelName || '-'}</td>
                      <td>${typeof provider.cost === 'number' ? provider.cost.toFixed(4) : '0.0000'}</td>
                      <td>${typeof provider.aCost === 'number' ? provider.aCost.toFixed(4) : '0.0000'}</td>
                      <td>{provider.latency}ms</td>
                      <td>
                        {provider.voiceCount > 0 ? (
                          <button size="sm" className="btn-style-border" onClick={() => toggleProviderVoices(provider._id)}>
                            {provider.voiceCount} voices {expandedProvider === provider._id ? <i className="fa fa-angle-up ml-2"></i> : <i className="fa fa-angle-down ml-2"></i>}
                          </button>
                        ) : <span className="text-muted">-</span> }
                      </td>
                      <td>{provider.agentCount || 0}</td>
                      <td>
                        <div className="action-box">
                          <UncontrolledDropdown>
                            <DropdownToggle color="secondary" size="sm">
                              <i className="fa fa-ellipsis-h"></i>
                            </DropdownToggle>
                            <DropdownMenu right>
                              <DropdownItem onClick={() => openProviderModal(provider)}>
                                <i className="fa fa-edit mr-2"></i> Edit
                              </DropdownItem>
                              <DropdownItem onClick={() => openVoiceModal(null, provider._id)}>
                                <i className="fa fa-plus mr-2"></i> Add Voice
                              </DropdownItem>
                              <DropdownItem
                                className="text-danger"
                                onClick={() => {
                                  setDeleteModal(true);
                                  setProviderToDelete(provider);
                                }}
                              >
                                <i className="fa fa-trash mr-2"></i> Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      </td>
                    </tr>

                    {/* Expandable Voices Row */}
                    {expandedProvider === provider._id && (
                      <tr>
                        <td colSpan="9" style={{ background: '#f8f9fa' }}>
                          <Collapse isOpen={true}>
                            <div className="p-3">
                              <div className="mb-3 d-flex justify-content-between w-100">
                                <h6 className="mb-0">Voices for {provider.label}</h6>
                                <button type="button" className="btn-style-new ms-3" onClick={() => openVoiceModal(null, provider._id)}>
                                  <i className="fa fa-plus mr-1"></i> Add Voice
                                </button>
                              </div>
                              {voices[provider._id] && voices[provider._id].length > 0 ? (
                                <div className="row">
                                  {voices[provider._id].map(voice => (
                                    <div key={voice._id} className="col-md-4 provider-card-voice">
                                      <Card>
                                        <CardBody>
                                          <div className="d-flex align-items-start">
                                            {voice.voiceImg ?
                                              <img className="me-3" src={voice.voiceImg} alt={voice.voiceName} style={{ width: '50px', height: '50px', borderRadius: '8px', objectFit: 'cover' }} />
                                            :
                                              <div className="me-3" style={{ width: '50px', height: '50px', background: '#e0e0e0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className="bi bi-person" style={{ fontSize: '24px' }}></i>
                                              </div>
                                            }
                                            <div className="flex-grow-1">
                                              <strong>{voice.voiceName}</strong>
                                              <div className="text-muted small">
                                                {voice.gender} • {voice.accent || 'No accent'}
                                              </div>
                                              <div className="text-muted small"> Key: {voice.voiceKey} </div>
                                              <div className="mt-2 d-flex gap-2">
                                                <button
                                                  type="button"
                                                  className="btn-style-border mr-3"
                                                  style={{ fontSize: '12px', padding: '4px 10px' }}
                                                  onClick={() => openVoiceModal(voice)}
                                                >
                                                  <i className="fa fa-edit mr-1"></i> Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn-style-border"
                                                  style={{ fontSize: '12px', padding: '4px 10px' }}
                                                  onClick={() => {
                                                    setVoiceToDelete({ voice, providerId: provider._id });
                                                    setVoiceDeleteModal(true);
                                                  }}
                                                >
                                                  <i className="fa fa-trash mr-1"></i> Delete
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </CardBody>
                                      </Card>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted">No voices configured for this provider</p>
                              )}
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              <span className="mx-3 align-self-center">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Provider Modal */}
      <Modal isOpen={showProviderModal} toggle={() => setShowProviderModal(false)} size="lg" className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowProviderModal(false)}>
          <h3>{selectedProvider ? 'Edit Provider' : 'Create Provider'}</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4 add-provider-content'>
            <Form>
              <FormGroup>
                <Label>Provider Icon</Label>
                <div className="d-flex align-items-start gap-3">
                  <div style={{ position: 'relative' }}>
                    {iconPreview ? (
                      <div style={{ position: 'relative' }}>
                        <img
                          src={iconPreview}
                          alt="Icon preview"
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e0e0e0' }}
                        />
                        <button
                          type="button"
                          className="action-btn"
                          style={{ position: 'absolute', lineHeight: '16px', top: '-8px', right: '-8px', backgroundColor: '#EF4D56', color: 'white', width: '28px', height: '28px' }}
                          onClick={() => { setIconFile(null); setIconPreview(null); }}
                          title="Remove icon"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100px',
                          height: '100px',
                          border: '2px dashed #ccc',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f8f9fa',
                          cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('provider-icon-input').click()}
                      >
                        <div className="text-center">
                          <i className="bi bi-cloud-upload" style={{ fontSize: '32px', color: '#999' }}></i>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>Click to upload</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <Input
                      id="provider-icon-input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/svg+xml,image/gif"
                      onChange={handleIconUpload}
                      style={{ display: 'none' }}
                    />
                    <div className="right-area ml-4">
                      <div className="">
                        <small className="text-muted d-block">
                          <i className="bi bi-info-circle"></i> Accepted: JPG, PNG, SVG, GIF
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-file-earmark"></i> Maximum size: 5MB
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn-style-border mt-3"
                        onClick={() => document.getElementById('provider-icon-input').click()}
                      >
                        <i className="fa fa-upload mr-1"></i> {iconPreview ? 'Change Icon' : 'Upload Icon'}
                      </button>
                    </div>

                  </div>
                </div>
              </FormGroup>

              <FormGroup>
                <label>Provider Label *</label>
                <Input
                  name="label"
                  value={providerForm.label}
                  onChange={handleProviderChange}
                  placeholder="e.g., GPT-4o Mini"
                />
              </FormGroup>

              <FormGroup>
                <label>Model Name</label>
                <Input
                  name="modelName"
                  value={providerForm.modelName}
                  onChange={handleProviderChange}
                  placeholder="e.g., openai/gpt-4o-mini"
                />
              </FormGroup>

              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <label>Provider *</label>
                    <select
                      name="provider"
                      className="form-select"
                      value={providerForm.provider}
                      onChange={handleProviderChange}
                    >
                      <option value="OpenRouter">OpenRouter</option>
                      <option value="Deepgram">Deepgram</option>
                      <option value="Cartesia">Cartesia</option>
                      <option value="ElevenLabs">ElevenLabs</option>
                    </select>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <label>Type *</label>
                    <select
                      name="type"
                      className="form-select"
                      value={providerForm.type}
                      onChange={handleProviderChange}
                    >
                      <option value="text">Text/LLM</option>
                      <option value="tts">Text-to-Speech</option>
                      <option value="stt">Speech-to-Text</option>
                      <option value="engine">Engine</option>
                      <option value="voip">VoIP</option>
                    </select>
                  </FormGroup>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <label>Cost (per unit) *</label>
                    <Input
                      type="number"
                      step="0.0001"
                      name="cost"
                      value={providerForm.cost}
                      onChange={handleProviderChange}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <label>Currency</label>
                    <select
                      name="currency"
                      className="form-select"
                      value={providerForm.currency}
                      onChange={handleProviderChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </FormGroup>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <label>Latency (ms) *</label>
                    <Input
                      type="number"
                      name="latency"
                      value={providerForm.latency}
                      onChange={handleProviderChange}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <label>Actual Cost</label>
                    <Input
                      type="number"
                      step="0.0001"
                      name="aCost"
                      value={providerForm.aCost}
                      onChange={handleProviderChange}
                    />
                  </FormGroup>
                </div>
              </div>
            </Form>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn-style-border" onClick={() => setShowProviderModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn-style-new" onClick={handleProviderSubmit}>
            <i className="fa fa-save mr-1"></i> {selectedProvider ? 'Update' : 'Create'} Provider
          </button>
        </ModalFooter>
      </Modal>

      {/* Voice Modal */}
      <Modal isOpen={showVoiceModal} toggle={() => setShowVoiceModal(false)} size="lg" className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setShowVoiceModal(false)}>
          <h3>{selectedVoice ? 'Edit Voice' : 'Create Voice'}</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4 add-provider-content'>
            <Form>
              <FormGroup>
                <Label>Select Provider *</Label>
                <select
                  name="providerId"
                  className="form-select"
                  value={voiceForm.providerId}
                  onChange={(e) => setVoiceForm({ ...voiceForm, providerId: e.target.value })}
                  disabled={selectedVoice !== null}
                >
                  <option value="">Choose provider...</option>
                  {providers
                    .filter(p => p.type === 'tts' || p.type === 'engine')
                    .map(p => (
                      <option key={p._id} value={p._id}>{p.label} - {p.provider}</option>
                    ))
                  }
                </select>
              </FormGroup>

              <FormGroup>
                <Label>Voice Character Image</Label>
                <div className="d-flex align-items-start gap-3">
                  <div style={{ position: 'relative' }}>
                    {voiceImagePreview ? (
                      <div style={{ position: 'relative' }}>
                        <img
                          src={voiceImagePreview}
                          alt="Voice preview"
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%', border: '2px solid #e0e0e0' }}
                        />
                        <button
                          type="button"
                          className="action-btn"
                          style={{ position: 'absolute', top: '6px', lineHeight: '16px', right: '-4px', backgroundColor: '#EF4D56', color: 'white', width: '28px', height: '28px' }}
                          onClick={() => { setVoiceImageFile(null); setVoiceImagePreview(null); }}
                          title="Remove image"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100px',
                          height: '100px',
                          border: '2px dashed #ccc',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f8f9fa',
                          cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('voice-image-input').click()}
                      >
                        <div className="text-center">
                          <i className="bi bi-person-circle" style={{ fontSize: '32px', color: '#999' }}></i>
                          <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>Upload</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <Input
                      id="voice-image-input"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate file size
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('File too large. Maximum size is 5MB');
                            return;
                          }
                          setVoiceImageFile(file);
                          setVoiceImagePreview(URL.createObjectURL(file));
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <div className="right-area ml-3 mt-2">
                      <div className="">
                        <small className="text-muted d-block">
                          <i className="bi bi-info-circle"></i> Accepted: JPG, PNG, GIF
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-file-earmark"></i> Maximum size: 5MB
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn-style-border mt-2"
                        onClick={() => document.getElementById('voice-image-input').click()}
                      >
                        <i className="fa fa-upload mr-1"></i> {voiceImagePreview ? 'Change Image' : 'Upload Image'}
                      </button>
                    </div>

                  </div>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Voice Name *</Label>
                <Input
                  name="voiceName"
                  value={voiceForm.voiceName}
                  onChange={(e) => setVoiceForm({ ...voiceForm, voiceName: e.target.value })}
                  placeholder="e.g., British Lady Professional"
                />
              </FormGroup>

              <FormGroup>
                <Label>Voice Key (API Identifier) *</Label>
                <Input
                  name="voiceKey"
                  value={voiceForm.voiceKey}
                  onChange={(e) => setVoiceForm({ ...voiceForm, voiceKey: e.target.value })}
                  placeholder="e.g., british-lady-professional-v1"
                />
                <small className="text-muted">Must be unique</small>
              </FormGroup>

              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Gender *</Label>
                    <select
                      name="gender"
                      className="form-select"
                      value={voiceForm.gender}
                      onChange={(e) => setVoiceForm({ ...voiceForm, gender: e.target.value })}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Accent</Label>
                    <Input
                      name="accent"
                      value={voiceForm.accent}
                      onChange={(e) => setVoiceForm({ ...voiceForm, accent: e.target.value })}
                      placeholder="e.g., UK, US, Australian"
                    />
                  </FormGroup>
                </div>
              </div>

              <FormGroup>
                <Label>Demo Audio URL</Label>
                <Input
                  name="demoUrl"
                  value={voiceForm.demoUrl}
                  onChange={(e) => setVoiceForm({ ...voiceForm, demoUrl: e.target.value })}
                  placeholder="https://example.com/demo.mp3"
                />
                {voiceForm.demoUrl && (
                  <div className="mt-2">
                    <audio controls style={{ width: '100%' }}>
                      <source src={voiceForm.demoUrl} type="audio/mpeg" />
                    </audio>
                  </div>
                )}
              </FormGroup>
            </Form>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn-style-border" onClick={() => setShowVoiceModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn-style-new" onClick={handleVoiceSubmit}>
            <i className="fa fa-save mr-1"></i> {selectedVoice ? 'Update' : 'Create'} Voice
          </button>
        </ModalFooter>
      </Modal>

      {/* Delete Provider*/}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} size="lg" className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setDeleteModal(false)}>
          <h3>Delete Provider?</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4 add-provider-content'>
            <p>Are you sure you want to delete this provider?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn-style-border" onClick={() => setDeleteModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn-style-new" onClick={() => providerToDelete && handleProviderDelete(providerToDelete._id)}>
            Delete Provider
          </button>
        </ModalFooter>
      </Modal>

      {/* Voice Modal */}
      <Modal isOpen={voiceDeleteModal} toggle={() => setVoiceDeleteModal(false)} size="lg" className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalHeader toggle={() => setVoiceDeleteModal(false)}>
          <h3>Delete Voice?</h3>
        </ModalHeader>
        <ModalBody>
          <div className='content mt-4 add-provider-content'>
            <p>Are you sure you want to delete this Voice?</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn-style-border" onClick={() => setVoiceDeleteModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn-style-new" onClick={() => voiceToDelete && handleVoiceDelete(voiceToDelete.voice._id, voiceToDelete.providerId)}>
            Delete Voice
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Providers;
